import React from "react";
import { Download, ScrollText, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AUDIT_ACTION_GROUPS,
  AUDIT_ACTION_LABELS,
  AUDIT_DOCUMENT_LABELS,
  AUDIT_FIELD_LABELS,
  auditEntriesToCsv,
  fetchAuditLog,
  formatAuditValue,
  type AuditEntry,
  type AuditTarget,
} from "@/lib/audit/documentAudit";

const formatDate = (value: string) =>
  new Date(value).toLocaleString("sv-SE", { dateStyle: "medium", timeStyle: "short" });

interface Props {
  target: AuditTarget;
  /** Etikett på knappen (t.ex. "Ändringslogg för projektet") */
  buttonLabel?: string;
}

/** Visar vem som ändrat, tagit bort eller återställt dokument – med filter. */
export const AuditLogDialog: React.FC<Props> = ({ target, buttonLabel }) => {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [entries, setEntries] = React.useState<AuditEntry[]>([]);

  const [group, setGroup] = React.useState("all");
  const [docType, setDocType] = React.useState<string>(target.documentType ?? "all");
  const [from, setFrom] = React.useState("");
  const [to, setTo] = React.useState("");
  const [actor, setActor] = React.useState("");

  const projectWide = !target.documentType && !target.recordId;

  React.useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    const actions = AUDIT_ACTION_GROUPS.find((g) => g.value === group)?.actions ?? [];
    fetchAuditLog(target, {
      actions,
      documentType: docType === "all" ? undefined : docType,
      from: from || undefined,
      to: to || undefined,
      actor: actor.trim() || undefined,
    })
      .then((rows) => active && setEntries(rows))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group, docType, from, to, actor, target.documentType, target.recordId, target.projectId]);

  const exportCsv = () => {
    const blob = new Blob(["\uFEFF" + auditEntriesToCsv(entries)], {
      type: "text/csv;charset=utf-8",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "andringslogg.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <ScrollText className="h-4 w-4" />
          {buttonLabel ?? "Ändringslogg"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{target.title || "Ändringslogg"}</DialogTitle>
          <DialogDescription>
            Varje ändring, borttagning, återställning och permanent radering sparas med person,
            tidpunkt och vad som ändrades.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[170px] flex-1">
            <Select value={group} onValueChange={setGroup}>
              <SelectTrigger>
                <SelectValue placeholder="Händelse" />
              </SelectTrigger>
              <SelectContent>
                {AUDIT_ACTION_GROUPS.map((g) => (
                  <SelectItem key={g.value} value={g.value}>
                    {g.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {projectWide && (
            <div className="min-w-[170px] flex-1">
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger>
                  <SelectValue placeholder="Dokumenttyp" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alla dokumenttyper</SelectItem>
                  {Object.entries(AUDIT_DOCUMENT_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Input
            type="date"
            className="w-[150px]"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            aria-label="Från datum"
          />
          <Input
            type="date"
            className="w-[150px]"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            aria-label="Till datum"
          />
          <Input
            className="w-[170px]"
            placeholder="Sök person"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
          />

          <Button
            variant={group === "deletions" ? "default" : "outline"}
            size="sm"
            onClick={() => setGroup(group === "deletions" ? "all" : "deletions")}
          >
            <Trash2 className="mr-2 h-4 w-4" /> Visa bara raderingar
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={entries.length === 0}>
            <Download className="mr-2 h-4 w-4" /> CSV
          </Button>
        </div>

        <ScrollArea className="max-h-[55vh] pr-3">
          {loading && <p className="py-6 text-sm text-muted-foreground">Hämtar ändringar…</p>}

          {!loading && entries.length === 0 && (
            <p className="py-6 text-sm text-muted-foreground">
              Inga händelser matchar filtret.
            </p>
          )}

          <ul className="space-y-3">
            {entries.map((entry) => {
              const fields = Object.entries(entry.changes || {});
              return (
                <li key={entry.id} className="rounded-xl border border-border/60 bg-card p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      {AUDIT_ACTION_LABELS[entry.action] ?? entry.action}
                      {" av "}
                      {entry.actor_name || "okänd användare"}
                      {projectWide && (
                        <Badge variant="secondary">
                          {AUDIT_DOCUMENT_LABELS[entry.document_type] ?? entry.document_type}
                          {entry.doc_number ? ` · Dok ${entry.doc_number}` : ""}
                        </Badge>
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(entry.created_at)}
                    </span>
                  </div>
                  {fields.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                      {fields.map(([field, diff]) => (
                        <li key={field}>
                          <span className="font-medium text-foreground">
                            {AUDIT_FIELD_LABELS[field] ?? field}:
                          </span>{" "}
                          {formatAuditValue(diff?.from)} → {formatAuditValue(diff?.to)}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default AuditLogDialog;
